// BL-25 — the Terms and Privacy editor. A Super Admin writes a draft, previews
// it exactly as the client app will read it, and publishes it. Publishing is
// permanent (0302): the words of any version that was ever live are frozen,
// because client consents name that version. To change a live document, start
// a new version from it.

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@bednbody/ui/components/alert-dialog";
import { PageHeader } from "@bednbody/ui/components/page-header";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { Field, FieldDescription, FieldLabel } from "@bednbody/ui/components/field";
import { Input } from "@bednbody/ui/components/input";
import { SurfaceCard } from "@bednbody/ui/components/surface-card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@bednbody/ui/components/tabs";
import { Textarea } from "@bednbody/ui/components/textarea";
import { cn } from "@bednbody/ui/lib/utils";
import { CopyIcon, PlusIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";
import { LegalPreview } from "./LegalPreview";
import {
  isEditable,
  type LegalDocumentRow,
  type LegalKind,
  type LegalVersionState,
  useLegalDocuments,
  usePublishLegalDocument,
  useSaveLegalDraft,
  versionState,
} from "./legal.hooks";

const KIND_LABEL: Record<LegalKind, string> = {
  terms: "Terms and Conditions",
  privacy: "Privacy Policy",
};

const STATE_BADGE: Record<
  LegalVersionState,
  { label: string; variant: "success-soft" | "muted-soft" | "info-soft" }
> = {
  published: { label: "Published", variant: "success-soft" },
  retired: { label: "Retired", variant: "muted-soft" },
  draft: { label: "Draft", variant: "info-soft" },
};

function errorText(error: unknown) {
  return error instanceof Error ? error.message : "Something went wrong.";
}

/** What the editor shows: a saved version, or a new draft not saved yet. */
type Selection = { kind: "saved"; id: string } | { kind: "new" };

function LegalEditor({ kind }: { kind: LegalKind }) {
  const { data: versions, isLoading, isError } = useLegalDocuments(kind);
  const save = useSaveLegalDraft(kind);
  const publish = usePublishLegalDocument(kind);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [label, setLabel] = useState("");
  const [body, setBody] = useState("");
  const [confirming, setConfirming] = useState(false);

  const selected =
    selection?.kind === "saved" ? (versions?.find((v) => v.id === selection.id) ?? null) : null;
  const editable = selection?.kind === "new" || (selected !== null && isEditable(selected));
  const dirty =
    selection?.kind === "new" ||
    (selected !== null && (label !== selected.version_label || body !== selected.body));
  const trimmedLabel = label.trim();
  const valid = trimmedLabel.length > 0 && trimmedLabel.length <= 40 && body.trim().length > 0;

  function open(row: LegalDocumentRow) {
    setSelection({ kind: "saved", id: row.id });
    setLabel(row.version_label);
    setBody(row.body);
  }

  function startNew(fromBody = "") {
    setSelection({ kind: "new" });
    setLabel("");
    setBody(fromBody);
  }

  async function saveDraft() {
    if (!valid) return;
    try {
      const id = await save.mutateAsync({
        id: selection?.kind === "saved" ? selection.id : null,
        versionLabel: label,
        body,
      });
      setSelection({ kind: "saved", id });
      setLabel(trimmedLabel);
      toast.success("Draft saved.");
    } catch (error) {
      toast.error("Couldn't save the draft", { description: errorText(error) });
    }
  }

  async function confirmPublish() {
    if (selection?.kind !== "saved") return;
    try {
      await publish.mutateAsync(selection.id);
      setConfirming(false);
      toast.success(`${KIND_LABEL[kind]} ${label} is live.`, {
        description: "New sign-ups now agree to this version.",
      });
    } catch (error) {
      toast.error("Couldn't publish", { description: errorText(error) });
    }
  }

  if (isError) {
    return <p className="text-sm text-destructive">Couldn&rsquo;t load the versions.</p>;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
      <SurfaceCard className="space-y-3">
        <Button className="w-full" onClick={() => startNew()} variant="outline">
          <PlusIcon /> New version
        </Button>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : versions && versions.length > 0 ? (
          <ul aria-label={`${KIND_LABEL[kind]} versions`} className="space-y-2">
            {versions.map((row) => {
              const state = STATE_BADGE[versionState(row)];
              const active = selection?.kind === "saved" && selection.id === row.id;
              return (
                <li key={row.id}>
                  <Button
                    aria-pressed={active}
                    className={cn("h-auto w-full justify-between gap-2 py-2", active && "bg-muted")}
                    onClick={() => open(row)}
                    variant="ghost"
                  >
                    <span className="truncate font-mono text-sm">{row.version_label}</span>
                    <Badge variant={state.variant}>{state.label}</Badge>
                  </Button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            No versions yet. The client app shows its bundled text until one is published.
          </p>
        )}
      </SurfaceCard>

      {selection === null ? (
        <SurfaceCard>
          <p className="text-sm text-muted-foreground">
            Pick a version to read it, or start a new one.
          </p>
        </SurfaceCard>
      ) : (
        <div className="grid gap-4 xl:h-[44rem] xl:grid-cols-2">
          <SurfaceCard className="flex min-h-0 flex-col gap-4">
            <Field>
              <FieldLabel htmlFor={`${kind}-version`}>Version label</FieldLabel>
              <Input
                disabled={!editable}
                id={`${kind}-version`}
                maxLength={40}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="2026-10-01"
                type="text"
                value={label}
              />
              <FieldDescription>
                What a client&rsquo;s consent records. Each label is used once.
              </FieldDescription>
            </Field>
            <Field className="min-h-0 flex-1">
              <FieldLabel htmlFor={`${kind}-body`}>Text</FieldLabel>
              <Textarea
                className="min-h-80 flex-1 resize-none overflow-y-auto font-mono text-sm field-sizing-fixed"
                disabled={!editable}
                id={`${kind}-body`}
                onChange={(event) => setBody(event.target.value)}
                value={body}
              />
              <FieldDescription>
                Markdown: <span className="font-mono">#</span> headings,{" "}
                <span className="font-mono">-</span> or <span className="font-mono">1.</span> lists,{" "}
                <span className="font-mono">**bold**</span>. Nothing else is rendered.{" "}
                <span className="tabular-nums">
                  {body.trim().split(/\s+/).filter(Boolean).length.toLocaleString()} words
                </span>
              </FieldDescription>
            </Field>

            {editable ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  disabled={!valid || !dirty || save.isPending}
                  onClick={() => void saveDraft()}
                  variant="outline"
                >
                  Save draft
                </Button>
                <Button
                  disabled={selection.kind !== "saved" || dirty || publish.isPending}
                  onClick={() => setConfirming(true)}
                  title={dirty ? "Save the draft first" : undefined}
                >
                  Publish
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  This version was published, so its words are frozen — clients agreed to them.
                </p>
                <Button onClick={() => startNew(body)} variant="outline">
                  <CopyIcon /> New version from this text
                </Button>
              </div>
            )}
          </SurfaceCard>

          <SurfaceCard className="flex min-h-0 flex-col gap-2 max-xl:max-h-[30rem]">
            <h2 className="text-sm font-semibold text-foreground">Preview</h2>
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <LegalPreview body={body} />
            </div>
          </SurfaceCard>
        </div>
      )}

      <AlertDialog onOpenChange={setConfirming} open={confirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Publish {KIND_LABEL[kind]} <span className="font-mono">{label}</span>?
            </AlertDialogTitle>
            <AlertDialogDescription>
              It replaces the live version for every new sign-up. Once published, its words can
              never be changed or deleted — a correction is a new version.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={publish.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction loading={publish.isPending} onClick={() => void confirmPublish()}>
              Publish
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function LegalPage() {
  return (
    <div className="space-y-8">
      <PageHeader
        description="The documents clients agree to at sign-up. The client app shows the published version."
        title="Terms and Privacy"
      />
      <Tabs defaultValue="terms">
        <TabsList
          className="w-full min-w-0 max-w-full justify-start overflow-x-auto border-b border-border"
          variant="line"
        >
          <TabsTrigger className="flex-none px-3" value="terms">
            {KIND_LABEL.terms}
          </TabsTrigger>
          <TabsTrigger className="flex-none px-3" value="privacy">
            {KIND_LABEL.privacy}
          </TabsTrigger>
        </TabsList>
        <TabsContent className="pt-4" value="terms">
          <LegalEditor kind="terms" />
        </TabsContent>
        <TabsContent className="pt-4" value="privacy">
          <LegalEditor kind="privacy" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
